<template>
  <div>
    <vue-element-loading :active="isSubmit" />
    <v-card>
      <v-card-title>
      <v-icon color="primary">
        {{getIcon(userType)}}
      </v-icon>
        <span class="px-2">{{$t(capitalizeFirstLetter(userType))}}</span>
      </v-card-title>
      <v-tabs v-model="active_tab" show-arrows class="my-2">
        <v-tab v-for="tab in tabs" :key="tab.idx">
          <v-icon size="20" class="me-3">
            {{ tab.icon }}
          </v-icon>
          <span>{{ $t(tab.title) }}</span>
        </v-tab>
      </v-tabs>

      <!-- Working status filter and Add Driver button -->
      <v-row v-if="userType === 'drivers'" class="mx-4 my-2 align-center">
        <v-col cols="12" sm="4" md="3">
          <v-select
            v-model="workingStatusFilter"
            :items="workingStatusItems"
            :label="$t('working_status_filter') || 'فلتر حالة التشغيل'"
            outlined
            dense
            hide-details
          ></v-select>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="auto">
          <v-btn color="primary" @click="openAddDriverModal">
            <v-icon class="me-2">mdi-plus</v-icon>
            إضافة سائق
          </v-btn>
        </v-col>
      </v-row>

      <!-- tabs item -->
      <v-tabs-items  v-model="active_tab">
        <!-- active -->
        <v-tab-item>
          <users-table :users="activeUsers" :userType="userType" :isLoading="isLoading"
          :tab="active_tab"
          @view-user="viewUser" @edit-user="editUser" @suspend-user="suspendActivateUser"
          @unassign-bus="unAssignBus" @assign-bus="assignBus"
          @view-driver-details="viewDriverDetails"
          @search-change="onSearchChange"></users-table>
        </v-tab-item>

        <!-- suspended -->
        <v-tab-item>
          <users-table :users="suspendedUsers" :userType="userType" :isLoading="isLoading"
          :tab="active_tab"
          @view-user="viewUser" @edit-user="editUser" @suspend-user="suspendActivateUser"
          @unassign-bus="unAssignBus" @assign-bus="assignBus"
          @view-driver-details="viewDriverDetails"
          @search-change="onSearchChange"></users-table>
        </v-tab-item>

        <v-tab-item>
          <users-table v-if="userType === 'drivers'" :users="underReviewUsers" :isLoading="isLoading"
          :userType="userType"
          :tab="active_tab"
          @view-user="viewUser"
          @view-driver-details="viewDriverDetails"
          @search-change="onSearchChange"
          ></users-table>
        </v-tab-item>

        <!-- rejected drivers tab -->
        <v-tab-item v-if="userType === 'drivers'">
          <users-table :users="rejectedUsers" :isLoading="isLoading"
          :userType="userType"
          :tab="active_tab"
          @view-user="viewUser"
          @view-driver-details="viewDriverDetails"
          @return-to-review="returnToReviewUser"
          @search-change="onSearchChange"
          ></users-table>
        </v-tab-item>

      </v-tabs-items>
    </v-card>
    <v-dialog v-if="selectedDriver" v-model="busesDialog" max-width="390">
      <v-card>
        <v-card-title class="text-h5"> Select bus for '{{ selectedDriver.name}}' </v-card-title>

        <v-card-text>
          <v-list dense>
            <v-subheader>Buses</v-subheader>
            <v-list-item-group>
              <v-list-item
                v-for="(bus, i) in availableBuses"
                :key="i"
              >
                <v-list-item-content @click="assignBusToDriver(bus)">
                  <v-list-item-title v-text="'License: ' + bus.license"></v-list-item-title>
                  <v-list-item-subtitle v-text="'Capacity: ' + bus.capacity"></v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-card-text>
        <v-container style="height: 400px">
          <v-row
            v-show="loadingBuses"
            class="fill-height"
            align-content="center"
            justify="center"
          >
            <v-col class="text-subtitle-1 text-center" cols="12">
              Please wait ...
            </v-col>
            <v-col cols="6">
              <v-progress-linear
                :active="loadingBuses"
                color="primary"
                indeterminate
                rounded
                height="6"
              ></v-progress-linear>
            </v-col>
          </v-row>
        </v-container>
        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn
            color="green darken-1"
            text
            @click="closeBusDialog"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Driver Details Dialog -->
    <v-dialog v-model="driverDetailsDialog" max-width="800">
      <v-card v-if="detailedDriver">
        <v-card-title class="headline primary white--text">
          <v-icon color="white" class="me-2">mdi-account-details</v-icon>
          {{ $t('driver_details') || 'بيانات السائق' }} - {{ detailedDriver.name }}
        </v-card-title>

        <!-- Warning Banner if registration is incomplete -->
        <v-alert
          v-if="!detailedDriver.driver_information || !detailedDriver.driver_information.first_name"
          type="warning"
          class="ma-4 mb-0"
          dense
          outlined
        >
          لا توجد بيانات تسجيل مكتملة لهذا السائق
        </v-alert>

        <v-tabs v-model="driverTab" grow>
          <v-tab>{{ $t('basic_info') }}</v-tab>
          <v-tab>{{ $t('vehicle_info') }}</v-tab>
          <v-tab>{{ $t('earning_percentage') }}</v-tab>
          <v-tab>{{ $t('documents') }}</v-tab>
          <v-tab>{{ $t('operation_log') }}</v-tab>
        </v-tabs>

        <v-tabs-items v-model="driverTab">
          <!-- Basic Info Tab -->
          <v-tab-item class="pa-4">
            <v-row>
              <v-col cols="12" md="4" class="d-flex justify-center align-center">
                <v-avatar size="120" class="elevation-2">
                  <v-img :src="getDriverAvatarUrl(detailedDriver)"></v-img>
                </v-avatar>
              </v-col>
              <v-col cols="12" md="8">
                <v-row>
                  <v-col cols="12" sm="6">
                    <strong>الاسم الأول:</strong> {{ (detailedDriver.driver_information && detailedDriver.driver_information.first_name) || '-' }}
                  </v-col>
                  <v-col cols="12" sm="6">
                    <strong>الاسم الأخير:</strong> {{ (detailedDriver.driver_information && detailedDriver.driver_information.last_name) || '-' }}
                  </v-col>
                  <v-col cols="12" sm="6">
                    <strong>الاسم الكامل:</strong> {{ detailedDriver.name || '-' }}
                  </v-col>
                  <v-col cols="12" sm="6">
                    <strong>الهاتف:</strong> {{ (detailedDriver.driver_information && detailedDriver.driver_information.phone_number) || detailedDriver.tel_number || '-' }}
                  </v-col>
                  <v-col cols="12" sm="6">
                    <strong>البريد الإلكتروني:</strong> {{ (detailedDriver.driver_information && detailedDriver.driver_information.email) || detailedDriver.email || '-' }}
                  </v-col>
                  <v-col cols="12" sm="6">
                    <strong>رخصة القيادة:</strong> {{ (detailedDriver.driver_information && detailedDriver.driver_information.license_number) || '-' }}
                  </v-col>
                  <v-col cols="12" sm="6">
                    <strong>العنوان:</strong> {{ (detailedDriver.driver_information && detailedDriver.driver_information.address) || detailedDriver.address || '-' }}
                  </v-col>
                  <v-col cols="12" sm="6">
                    <strong>الحالة:</strong>
                    <v-chip x-small :color="getStatusColor(detailedDriver.status_id)" dark>
                      {{ $t(getStatusValue(detailedDriver.status_id)) }}
                    </v-chip>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <strong>تاريخ التسجيل:</strong> {{ formatDateSafe(detailedDriver.created_at, 'YYYY-MM-DD HH:mm') }}
                  </v-col>
                  <v-col cols="12" sm="6">
                    <strong>{{ $t('driver_rating') || 'تقييم السائق' }}:</strong>
                    <span class="ms-2 font-weight-bold" :style="{ color: hasRating(detailedDriver) ? '#d97706' : '#6b7280' }">
                      <v-icon x-small :color="hasRating(detailedDriver) ? '#f59e0b' : '#9ca3af'" class="me-1">mdi-star</v-icon>
                      {{ formatRating(detailedDriver) }}
                      <span v-if="hasRating(detailedDriver)" class="grey--text text--darken-1 ms-1 font-weight-normal">({{ detailedDriver.ratings_count }})</span>
                    </span>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <strong>{{ $t('current_month_distance') || 'المسافة هذا الشهر' }}:</strong>
                    <span class="ms-2 font-weight-bold primary--text">
                      <v-icon x-small color="primary" class="me-1">mdi-bus</v-icon>
                      {{ (detailedDriver.monthly_distance_km !== null && detailedDriver.monthly_distance_km !== undefined) ? (detailedDriver.monthly_distance_km + ' ' + ($t('km') || 'كم')) : (detailedDriver.driver_statistics && detailedDriver.driver_statistics.current_month_distance_km !== null ? detailedDriver.driver_statistics.current_month_distance_km + ' ' + ($t('km') || 'كم') : '--') }}
                    </span>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-tab-item>

          <!-- Vehicle Info Tab -->
          <v-tab-item class="pa-4">
            <div v-if="detailedDriver.driver_information && detailedDriver.driver_information.driver_type">
              <v-row>
                <v-col cols="12">
                  <strong>نوع التسجيل:</strong>
                  <v-chip color="secondary" small class="ms-2">
                    {{ (detailedDriver.driver_information && detailedDriver.driver_information.driver_type === 'with_vehicle') ? 'سائق بسيارة' : 'سائق بدون سيارة' }}
                  </v-chip>
                </v-col>
              </v-row>

              <v-row v-if="detailedDriver.driver_information && detailedDriver.driver_information.driver_type === 'with_vehicle'" class="mt-2">
                <v-col cols="12" sm="6">
                  <strong>نوع المركبة:</strong> {{ (detailedDriver.driver_information && detailedDriver.driver_information.vehicle_type) || '-' }}
                </v-col>
                <v-col cols="12" sm="6">
                  <strong>رقم لوحة المركبة:</strong> {{ (detailedDriver.driver_information && detailedDriver.driver_information.vehicle_license_number) || '-' }}
                </v-col>
                <v-col cols="12" sm="6">
                  <strong>الباص المرتبط به:</strong>
                  <v-chip v-if="detailedDriver.bus" color="info" small class="ms-2">
                    {{ detailedDriver.bus.license }}
                  </v-chip>
                  <span v-else class="text--secondary ms-2">-</span>
                </v-col>
              </v-row>

              <div v-else class="text-center py-6 text--secondary">
                <v-icon size="40" color="grey">mdi-car-off</v-icon>
                <div class="mt-2">لا توجد مركبة خاصة مسجلة</div>
              </div>
            </div>
            <div v-else class="text-center py-6 text--secondary">
              <v-icon size="40" color="grey">mdi-car-off</v-icon>
              <div class="mt-2">لا توجد بيانات مركبة مسجلة</div>
            </div>
          </v-tab-item>

          <!-- Earning Percentage Tab -->
          <v-tab-item class="pa-4">
            <v-row>
              <v-col cols="12">
                <div class="text-subtitle-1 mb-2 font-weight-bold">نسبة أرباح السائق:</div>
                <v-card outlined class="pa-4 bg-light">
                  <div v-if="detailedDriver.driver_information && detailedDriver.driver_information.driver_earning_percentage !== null">
                    <div class="d-flex align-center">
                      <v-icon color="success" class="me-2">mdi-cash-multiple</v-icon>
                      <span class="text-h6 font-weight-bold">{{ detailedDriver.driver_information.driver_earning_percentage }}%</span>
                      <v-chip color="success" small class="ms-2">نسبة خاصة بالسائق</v-chip>
                    </div>
                  </div>
                  <div v-else>
                    <div class="d-flex align-center">
                      <v-icon color="warning" class="me-2">mdi-cog-outline</v-icon>
                      <span class="text-subtitle-1">يستخدم النسبة الافتراضية</span>
                      <v-chip color="warning" small class="ms-2">النسبة الافتراضية</v-chip>
                    </div>
                    <div class="text-caption text--secondary mt-2">
                      (النسبة العامة: {{ (detailedDriver.driver_information && detailedDriver.driver_information.driver_type === 'with_vehicle') ? 'أرباح سائق بسيارة' : 'أرباح سائق بدون سيارة' }})
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-tab-item>

          <!-- Documents Tab -->
          <v-tab-item class="pa-4">
            <v-row>
              <v-col
                cols="12"
                md="6"
                v-for="docType in getExpectedDocuments(detailedDriver)"
                :key="docType"
                class="mb-4"
              >
                <v-card outlined class="h-full" :style="isDocRejectedDetailed(detailedDriver, docType) ? 'border: 2px solid #ef5350 !important; background: rgba(239,83,80,0.04);' : ''">
                  <v-card-title class="subtitle-2 font-weight-bold py-2 bg-light d-flex justify-space-between">
                    <span>{{ docType }}</span>
                    <v-chip v-if="isDocRejectedDetailed(detailedDriver, docType)" x-small color="error" dark>مرفوض</v-chip>
                  </v-card-title>
                  <v-card-text class="pt-3">
                    <div v-if="getDoc(detailedDriver, docType)">
                      <div class="d-flex">
                        <div class="me-3" v-if="getDoc(detailedDriver, docType) && getDoc(detailedDriver, docType).remote_file_path">
                          <v-img
                            :src="getDocUrl(getDoc(detailedDriver, docType))"
                            width="80"
                            height="80"
                            contain
                            class="bg-grey-lighten-4 rounded"
                            style="cursor: pointer;"
                            @click="openImage(getDocUrl(getDoc(detailedDriver, docType)))"
                          ></v-img>
                        </div>
                        <div>
                          <div>
                            <strong>رقم المستند:</strong> {{ getDoc(detailedDriver, docType).document_number || '-' }}
                          </div>
                          <div>
                            <strong>تاريخ الانتهاء:</strong> {{ formatDateSafe(getDoc(detailedDriver, docType) ? getDoc(detailedDriver, docType).expiry_date : null) }}
                          </div>
                          <div class="mt-1">
                            <v-chip v-if="isDocRejectedDetailed(detailedDriver, docType)" x-small color="error" dark>مرفوض</v-chip>
                            <v-chip v-else x-small color="success" dark>مرفوع</v-chip>
                          </div>
                        </div>
                      </div>
                      <v-card-actions class="px-0 pb-0 pt-2" v-if="getDoc(detailedDriver, docType) && getDoc(detailedDriver, docType).remote_file_path">
                        <v-btn text x-small color="primary" @click="openImage(getDocUrl(getDoc(detailedDriver, docType)))">
                          <v-icon x-small class="me-1">mdi-open-in-new</v-icon> عرض
                        </v-btn>
                        <v-btn text x-small color="secondary" :href="getDocUrl(getDoc(detailedDriver, docType))" target="_blank" download>
                          <v-icon x-small class="me-1">mdi-download</v-icon> تحميل
                        </v-btn>
                      </v-card-actions>
                    </div>
                    <div v-else class="d-flex align-center py-4">
                      <v-icon color="error" class="me-2">mdi-close-circle-outline</v-icon>
                      <span class="text--secondary">غير مرفوع</span>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-tab-item>

          <!-- Operation Log Tab -->
          <v-tab-item class="pa-4">
            <div v-if="loadingLog" class="text-center py-10">
              <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
              <div class="mt-2 text-subtitle-2 text--secondary">جاري تحميل سجل التشغيل...</div>
            </div>
            
            <v-alert v-else-if="logError" type="error" outlined class="ma-4">
              حدث خطأ أثناء تحميل سجل التشغيل. يرجى المحاولة مرة أخرى.
            </v-alert>
            
            <div v-else-if="operationLog">
              <!-- Summary Cards Grid -->
              <v-row class="mb-6">
                <v-col cols="12" sm="6" md="3" v-for="(card, index) in summaryCards" :key="index" class="mb-4">
                  <v-card class="mx-auto h-full" outlined>
                    <v-list-item two-line>
                      <v-list-item-content class="pa-2">
                        <div class="text-overline mb-1 grey--text font-weight-bold">
                          {{ card.title }}
                        </div>
                        <v-list-item-title class="text-h6 font-weight-bold" :class="card.color + '--text'">
                          {{ card.value }}
                        </v-list-item-title>
                      </v-list-item-content>
                      <v-avatar tile size="40" :color="card.bgColor" class="rounded">
                        <v-icon :color="card.color">{{ card.icon }}</v-icon>
                      </v-avatar>
                    </v-list-item>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Section 1: Trips details -->
              <div class="my-6">
                <h3 class="text-subtitle-1 font-weight-bold mb-3 primary--text">
                  <v-icon color="primary" class="me-1">mdi-bus-school</v-icon>
                  {{ $t('completed_trips_details') }}
                </h3>
                <v-data-table
                  v-if="operationLog.trips && operationLog.trips.length > 0"
                  :headers="tripHeaders"
                  :items="operationLog.trips"
                  class="elevation-1"
                  hide-default-footer
                  disable-pagination
                >
                  <template v-slot:item.total_revenue="{ item }">
                    {{ item.total_revenue }} EGP
                  </template>
                  <template v-slot:item.driver_earnings="{ item }">
                    {{ item.driver_earnings }} EGP
                  </template>
                  <template v-slot:item.cash_collected="{ item }">
                    {{ item.cash_collected }} EGP
                  </template>
                  <template v-slot:item.company_net="{ item }">
                    {{ item.company_net }} EGP
                  </template>
                  <template v-slot:item.started_at="{ item }">
                    {{ formatDateSafe(item.started_at, 'YYYY-MM-DD HH:mm') }}
                  </template>
                  <template v-slot:item.ended_at="{ item }">
                    {{ formatDateSafe(item.ended_at, 'YYYY-MM-DD HH:mm') }}
                  </template>
                  <template v-slot:item.status="{ item }">
                    <v-chip color="success" x-small dark>مكتملة</v-chip>
                  </template>
                </v-data-table>
                <v-alert v-else type="info" outlined dense class="text-center py-4">
                  <v-icon class="me-2">mdi-alert-circle-outline</v-icon>
                  {{ $t('no_completed_trips') }}
                </v-alert>
              </div>

              <!-- Section 2: Routes worked on -->
              <div class="my-6" v-if="operationLog.routes && operationLog.routes.length > 0">
                <h3 class="text-subtitle-1 font-weight-bold mb-3 primary--text">
                  <v-icon color="primary" class="me-1">mdi-map-marker-distance</v-icon>
                  {{ $t('routes_worked_on') }}
                </h3>
                <v-data-table
                  :headers="routeHeaders"
                  :items="operationLog.routes"
                  class="elevation-1"
                  hide-default-footer
                  disable-pagination
                >
                  <template v-slot:item.total_revenue="{ item }">
                    {{ item.total_revenue }} EGP
                  </template>
                  <template v-slot:item.total_driver_earnings="{ item }">
                    {{ item.total_driver_earnings }} EGP
                  </template>
                  <template v-slot:item.last_trip_date="{ item }">
                    {{ formatDateSafe(item.last_trip_date) }}
                  </template>
                </v-data-table>
              </div>

              <!-- Section 3: Wallet transactions -->
              <div class="my-6">
                <h3 class="text-subtitle-1 font-weight-bold mb-3 primary--text">
                  <v-icon color="primary" class="me-1">mdi-wallet-outline</v-icon>
                  {{ $t('wallet_transactions') }}
                </h3>
                <v-data-table
                  v-if="operationLog.wallet_transactions && operationLog.wallet_transactions.length > 0"
                  :headers="transactionHeaders"
                  :items="operationLog.wallet_transactions"
                  class="elevation-1"
                  :footer-props="footerProps"
                >
                  <template v-slot:item.amount="{ item }">
                    <span :class="item.direction === 'credit' ? 'success--text font-weight-bold' : 'error--text font-weight-bold'">
                      {{ item.direction === 'credit' ? '+' : '-' }}{{ item.amount }} EGP
                    </span>
                  </template>
                  <template v-slot:item.type="{ item }">
                    <v-chip x-small outlined :color="getTransactionTypeColor(item.type)">
                      {{ getTransactionTypeName(item.type) }}
                    </v-chip>
                  </template>
                  <template v-slot:item.direction="{ item }">
                    {{ item.direction === 'credit' ? 'إيداع (Credit)' : 'سحب (Debit)' }}
                  </template>
                  <template v-slot:item.created_at="{ item }">
                    {{ formatDateSafe(item.created_at, 'YYYY-MM-DD HH:mm') }}
                  </template>
                  <template v-slot:item.planned_trip_id="{ item }">
                    {{ item.planned_trip_id || '-' }}
                  </template>
                  <template v-slot:item.status="{ item }">
                    <v-chip x-small :color="item.status === 'completed' ? 'success' : 'warning'" dark>
                      {{ item.status }}
                    </v-chip>
                  </template>
                </v-data-table>
                <div v-else class="text-center py-6 text--secondary">لا توجد معاملات محفظة مسجلة</div>
              </div>
            </div>
          </v-tab-item>
        </v-tabs-items>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" text @click="driverDetailsDialog = false">إغلاق</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Full Image Viewer Dialog -->
    <v-dialog v-model="imageViewerDialog" max-width="800">
      <v-card>
        <v-card-title class="py-2 bg-light">
          <span>معاينة الصورة</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="imageViewerDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-4 text-center">
          <img :src="viewerImageUrl" style="max-width: 100%; max-height: 80vh;" />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Add Driver Dialog -->
    <v-dialog v-model="addDriverDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="headline primary white--text">
          <v-icon color="white" class="me-2">mdi-account-plus</v-icon>
          إضافة سائق جديد
        </v-card-title>
        <v-card-text class="pt-4">
          <v-form ref="addDriverForm" v-model="isAddDriverFormValid">
            <v-row>
              <!-- Account info -->
              <v-col cols="12" class="pb-0">
                <span class="subtitle-1 font-weight-bold">بيانات الحساب</span>
                <v-divider class="my-2"></v-divider>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="newDriver.name"
                  label="الاسم *"
                  outlined
                  dense
                  required
                  :rules="[v => !!v || 'الاسم مطلوب']"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="newDriver.phone"
                  label="رقم الهاتف *"
                  outlined
                  dense
                  required
                  :rules="[v => !!v || 'رقم الهاتف مطلوب']"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="newDriver.email"
                  label="البريد الإلكتروني (اختياري)"
                  outlined
                  dense
                  type="email"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="newDriver.password"
                  label="كلمة المرور *"
                  outlined
                  dense
                  required
                  type="password"
                  :rules="[v => !!v || 'كلمة المرور مطلوبة', v => (v && v.length >= 6) || 'يجب أن تكون كلمة المرور 6 أحرف على الأقل']"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="newDriver.password_confirmation"
                  label="تأكيد كلمة المرور *"
                  outlined
                  dense
                  required
                  type="password"
                  :rules="[v => !!v || 'تأكيد كلمة المرور مطلوب', v => v === newDriver.password || 'كلمة المرور غير متطابقة']"
                ></v-text-field>
              </v-col>

              <!-- Driver info -->
              <v-col cols="12" class="pb-0 pt-4">
                <span class="subtitle-1 font-weight-bold">بيانات السائق</span>
                <v-divider class="my-2"></v-divider>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="newDriver.driver_type"
                  :items="driverTypeOptions"
                  label="نوع السائق *"
                  outlined
                  dense
                  required
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="newDriver.working_status"
                  :items="workingStatusOptions"
                  label="حالة التشغيل *"
                  outlined
                  dense
                  required
                ></v-select>
              </v-col>

              <!-- Vehicle info (Conditional) -->
              <template v-if="newDriver.driver_type === 'with_vehicle'">
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="newDriver.vehicle_type"
                    :items="vehicleTypeOptions"
                    label="نوع المركبة *"
                    outlined
                    dense
                    required
                    :rules="[v => !!v || 'نوع المركبة مطلوب']"
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="newDriver.vehicle_license_number"
                    label="رقم لوحة / رخصة المركبة *"
                    outlined
                    dense
                    required
                    :rules="[v => !!v || 'رقم الرخصة/اللوحة مطلوب']"
                  ></v-text-field>
                </v-col>
              </template>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="pb-4 pr-4 pl-4">
          <v-btn color="error" text @click="closeAddDriverModal" :disabled="isSavingDriver">إلغاء</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="success" :loading="isSavingDriver" :disabled="!isAddDriverFormValid || isSavingDriver" @click="saveNewDriver">
            إضافة السائق
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>

  import usersTable from './users-table.vue';
import auth from '@/services/AuthService';
import { Keys } from '@/config.js';
import moment from 'moment';
  import {
    mdiAccountCheck,
    mdiAccountOff,
    mdiAirplane,
    mdiMotionPause,
    mdiAccountClock,
    mdiAccountQuestion,
    mdiAccountCancel
  } from '@mdi/js'

import VueElementLoading from "vue-element-loading";

export default {
  components: {
    VueElementLoading,
    usersTable
  },
  data() {
    return {
      userType:'',
      workingStatusFilter: 'all',
      searchQuery: "",
      searchTimer: null,
      users: [],
      activeUsers: [],
      suspendedUsers: [],
      underReviewUsers: [],
      rejectedUsers: [],
      availableBuses: [],
      dialog: false,
      addDriverDialog: false,
      isAddDriverFormValid: false,
      isSavingDriver: false,
      newDriver: {
        name: "",
        phone: "",
        email: "",
        password: "",
        password_confirmation: "",
        driver_type: "without_vehicle",
        working_status: "active",
        vehicle_type: "",
        vehicle_license_number: ""
      },
      driverTypeOptions: [
        { text: "سائق بدون مركبة", value: "without_vehicle" },
        { text: "سائق بمركبة", value: "with_vehicle" }
      ],
      workingStatusOptions: [
        { text: "نشط", value: "active" },
        { text: "غير نشط", value: "inactive" }
      ],
      vehicleTypeOptions: [
        { text: "باص (Bus)", value: "bus" },
        { text: "ميني باص (Mini Bus)", value: "mini_bus" },
        { text: "كوستر (Coaster)", value: "coaster" },
        { text: "تويوتا هايس (Hiace)", value: "hiace" },
        { text: "سيارة ملاكي (Car)", value: "car" },
        { text: "أخرى (Other)", value: "other" }
      ],
      busesDialog: false,
      loadingBuses: false,
      isLoading: false,
      isSubmit: false,
      selectedUser: null,
      selectedDriver: null,
      tabs: [],
      driversTabs: [
        { idx: 0, title: "Active", icon: mdiAirplane },
        { idx: 1, title: "Suspended", icon: mdiMotionPause },
        { idx: 2, title: "Under Review", icon: mdiAccountClock },
        { idx: 3, title: "Rejected", icon: mdiAccountCancel },
      ],
      customersTabs: [
        { idx: 0, title: "Active", icon: mdiAirplane },
        { idx: 1, title: "Suspended", icon: mdiMotionPause },
      ],
      active_tab: null,
      driverDetailsDialog: false,
      detailedDriver: null,
      driverTab: null,
      imageViewerDialog: false,
      viewerImageUrl: '',
      operationLog: null,
      loadedDriverLogId: null,
      loadingLog: false,
      logError: false,
      tabNames: ['basic', 'vehicle', 'earning', 'documents', 'operation_log'],
      tripHeaders: [
        { text: 'رقم الرحلة', value: 'planned_trip_id', sortable: false },
        { text: 'التاريخ', value: 'planned_date' },
        { text: 'وقت البداية', value: 'started_at' },
        { text: 'وقت النهاية', value: 'ended_at' },
        { text: 'الخط', value: 'route_name' },
        { text: 'من', value: 'from' },
        { text: 'إلى', value: 'to' },
        { text: 'لوحة الباص', value: 'bus_license' },
        { text: 'الحجوزات', value: 'reservations_count' },
        { text: 'إجمالي الإيراد', value: 'total_revenue' },
        { text: 'أرباح السائق', value: 'driver_earnings' },
        { text: 'الكاش المحصل', value: 'cash_collected' },
        { text: 'صافي الشركة', value: 'company_net' },
        { text: 'الحالة', value: 'status' }
      ],
      routeHeaders: [
        { text: 'اسم الخط', value: 'route_name' },
        { text: 'الرحلات المكتملة', value: 'completed_trips_count' },
        { text: 'إجمالي الحجوزات', value: 'total_reservations_count' },
        { text: 'إجمالي الإيراد', value: 'total_revenue' },
        { text: 'إجمالي أرباح السائق', value: 'total_driver_earnings' },
        { text: 'آخر رحلة', value: 'last_trip_date' }
      ],
      transactionHeaders: [
        { text: 'التاريخ', value: 'created_at' },
        { text: 'النوع', value: 'type' },
        { text: 'الاتجاه', value: 'direction' },
        { text: 'المبلغ', value: 'amount' },
        { text: 'الوصف', value: 'description' },
        { text: 'الحالة', value: 'status' },
        { text: 'رقم الرحلة', value: 'planned_trip_id' }
      ],
      footerProps: {
        "items-per-page-options": [5, 10, 20, 50]
      },
    };
  },
  watch:{
      workingStatusFilter: function () {
        this.loadUsers();
      },
      $route (to, from){
          this.userType = to.name;
          this.workingStatusFilter = 'all'; // reset filter on page navigation
          this.updateTabs();
          if(this.userType === 'drivers')
          {
            this.active_tab = parseInt(localStorage.tabIdxDrivers) || 0;
          }
          else if(this.userType === 'customers')
          {
            this.active_tab = parseInt(localStorage.tabIdxCustomers) || 0;
          }
          this.loadUsers();
      },
      active_tab: function (newVal, oldVal) {
        if(this.userType === 'drivers')
        {
          localStorage.tabIdxDrivers = newVal;
        }
        else if(this.userType === 'customers')
        {
          localStorage.tabIdxCustomers = newVal;
        }
      },
      driverTab(val) {
        if (this.detailedDriver && this.tabNames[val] === 'operation_log') {
          if (this.loadedDriverLogId !== this.detailedDriver.id) {
            this.fetchDriverOperationLog(this.detailedDriver.id);
          }
        }
      },
      detailedDriver(newVal) {
        if (!newVal) {
          this.operationLog = null;
          this.loadedDriverLogId = null;
          this.logError = false;
          this.loadingLog = false;
        }
      },
  },
  mounted() {
    this.userType = this.$router.currentRoute.name
    this.updateTabs();
    if(this.userType === 'drivers')
    {
      this.active_tab = parseInt(localStorage.tabIdxDrivers) || 0;
    }
    else if(this.userType === 'customers')
    {
      this.active_tab = parseInt(localStorage.tabIdxCustomers) || 0;
    }
    this.loadUsers();
    //load buses for drivers
    if(this.userType === 'drivers'){
      this.loadAvailableBuses();
    }
  },
  methods: {
    updateTabs()
    {
      if(this.userType === 'drivers')
      {
        this.tabs = this.driversTabs;
      }
      else
      {
        this.tabs = this.customersTabs;
      }
    },
    capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    getIcon(userType) {
      switch (userType) {
        case 'admin':
          return 'mdi-account-lock'
          break;
        case 'customers':
          return 'mdi-account'
          break;
        case 'drivers':
          return 'mdi-account-tie-hat'
          break;
        default:
          break;
      }
    },
    onSearchChange(val) {
      this.searchQuery = val || "";
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.loadUsers();
      }, 400);
    },
    loadUsers() {
      this.isLoading = true;
      this.users = [];
      const params = {
        userType: this.userType,
        working_status: this.userType === 'drivers' ? this.workingStatusFilter : 'all'
      };
      if (this.searchQuery) {
        params.search = this.searchQuery;
      }
      axios
        .get('/users/all', { params })
        .then((response) => {
          this.users = response.data;
          this.activeUsers = this.users.filter(user => user.status_id === 1);
          this.suspendedUsers = this.users.filter(user => user.status_id === 3);
          this.underReviewUsers = this.users.filter(user => user.status_id === 4);
          this.rejectedUsers = this.users.filter(user => user.status_id === 5);
        })
        .catch((error) => {
          this.$notify({
            title: "Error",
            text: "Error while retrieving users",
            type: "error",
          });
          console.log(error);
          auth.checkError(error.response.data.message, this.$router, this.$swal);
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    viewUser(user)
    {
      this.$router.push({
        name: "view-user",
        params: {
          user_id: user.id,
        },
      });
    },
    editUser(user)
    {
      this.$router.push({
        name: "edit-user",
        params: {
          user_id: user.id,
        },
      });
    },
    suspendActivateUser(user, index) {
        const isActivating = user.status_id != 1;
        this.$swal
            .fire({
            title: this.$t(isActivating ? "activate_user" : "suspend_user"),
            text: this.$t(
                isActivating ? "confirm_activate_user" : "confirm_suspend_user",
                { name: user.name }
            ),
            icon: isActivating ? "success" : "error",
            showCancelButton: true,
            confirmButtonText: this.$t("yes"),
            cancelButtonText: this.$t("cancel"),
            })
            .then((result) => {
            if (result.isConfirmed) {
                this.suspendActivateUserServer(user, index);
            }
        });
    },
    suspendActivateUserServer(user, indexx) {
      this.isSubmit = true;
      axios
        .post('/users/suspend-activate', {
          user_id: user.id,
        })
        .then((response) => {
          this.isSubmit = false;
          //get the index
          let index = this.users.indexOf(user);
          this.users[index].status_id = user.status_id!=1 ? 1:3;
          this.activeUsers = this.users.filter(user => user.status_id === 1);
          this.suspendedUsers = this.users.filter(user => user.status_id === 3);
          this.$notify({
            title: "Success",
            text: "User " + (user.status_id!=1? "suspended" : "activated"),
            type: "success",
          });
        })
        .catch((error) => {
          this.isSubmit = false;
          this.$notify({
            title: "Error",
            text: "Error",
            type: "error",
          });
          this.$swal("Error", error.response.data.message, "error");
        });
    },
    loadAvailableBuses() {
      this.loadingBuses = true;
      this.availableBuses = [];
      axios
        .get('/drivers/available-buses')
        .then((response) => {
          this.availableBuses = response.data;
        })
        .catch((error) => {
          this.$notify({
            title: "Error",
            text: "Error while retrieving buses",
            type: "error",
          });
          console.log(error);
          this.$swal("Error", error.response.data.message, "error");
        })
        .then(() => {
          this.loadingBuses = false;
        });
    },
    assignBus(item) {
      this.selectedDriver = item;
      this.busesDialog = true;
      this.loadAvailableBuses()
    },
    closeBusDialog() {
      this.busesDialog = false;
      this.loadingBuses = false;
      this.availableBuses = [];
    },
    assignBusToDriver(bus) {
      this.loadingBuses = true;
      axios
        .post('/drivers/assign-bus', {
          driver_id: this.selectedDriver.id,
          bus_id: bus.id,
        })
        .then((response) => {
          console.log(response);
          this.loadingBuses = false;
          this.selectedDriver.bus = bus;
          this.closeBusDialog();
          this.$notify({
            title: "Success",
            text: "Bus assigned to driver",
            type: "success",
          });
        })
        .catch((error) => {
          this.isSubmit = false;
          this.$notify({
            title: "Error",
            text: "Error",
            type: "error",
          });
          console.log(error);
          this.$swal("Error", error.response.data.message, "error");
        })
        .then(() => {
          this.closeBusDialog();
        });
    },
    unAssignBus(item) {
        this.$swal
            .fire({
            title: this.$t("unassign_bus_title"),
            text: this.$t("unassign_bus_text", {
                name: item.name,
                license: item.bus.license
            }),
            icon: "error",
            showCancelButton: true,
            confirmButtonText: this.$t("yes_delete"),
            cancelButtonText: this.$t("cancel"),
            })
            .then((result) => {
            if (result.isConfirmed) {
                this.unassignBusFromDriver(item);
            }
        });
    },
    unassignBusFromDriver(driver) {
      this.isLoading = true;
      axios
        .post('/drivers/unassign-bus', {
          driver_id: driver.id,
        })
        .then((response) => {
          this.isLoading = false;
          driver.bus = null;
          this.$notify({
            title: "Success",
            text: "Bus unassigned from driver",
            type: "success",
          });
        })
        .catch((error) => {
          this.isSubmit = false;
          this.$notify({
            title: "Error",
            text: "Error",
            type: "error",
          });
          console.log(error);
          this.$swal("Error", error.response.data.message, "error");
        })
        .then(() => {
          this.closeBusDialog();
        });
    },
    async viewDriverDetails(driver) {
      if (!driver || !driver.id) return;
      this.isLoading = true;
      try {
        const response = await axios.get(`/users/user/${driver.id}`);
        if (response && response.data) {
          this.detailedDriver = response.data;
          this.driverTab = 0;
          this.driverDetailsDialog = true;
        }
      } catch (error) {
        this.$notify({
          title: "Error",
          text: "Error fetching driver data",
          type: "error",
        });
        console.error(error);
        this.driverDetailsDialog = false;
      } finally {
        this.isLoading = false;
      }
    },
    getApiUrl() {
      return Keys.VUE_APP_API_URL || '';
    },
    getExpectedDocuments(driver) {
      const baseDocs = ['رخصة القيادة', 'بطاقة الرقم القومي', 'صحيفة الحالة الجنائية', 'تحليل المخدرات', 'صورة شخصية'];
      if (driver && driver.driver_information && driver.driver_information.driver_type === 'with_vehicle') {
        baseDocs.splice(1, 0, 'رخصة المركبة');
      }
      return baseDocs;
    },
    getDoc(driver, type) {
      if (!driver || !driver.driver_information || !driver.driver_information.documents) return null;
      return driver.driver_information.documents.find(doc => doc.document_name === type);
    },
    isDocRejectedDetailed(driver, docType) {
      if (!driver || !driver.driver_information || !driver.driver_information.rejected_documents) return false;
      const doc = this.getDoc(driver, docType);
      const rejected = driver.driver_information.rejected_documents;
      if (!Array.isArray(rejected)) return false;
      return rejected.some(item => {
        if (typeof item === 'number' || typeof item === 'string') {
          return (doc && item == doc.id) || item == docType;
        }
        return (doc && item.id == doc.id) || item.name == docType;
      });
    },
    getDocUrl(doc) {
      if (!doc || !doc.remote_file_path) return '';
      return this.getApiUrl() + doc.remote_file_path;
    },
    formatDateSafe(value, format = 'YYYY-MM-DD') {
      if (!value || value === '1970-01-01' || value === '0000-00-00') return '-';
      const date = moment(value);
      if (!date.isValid()) return '-';
      return date.format(format);
    },
    openImage(url) {
      this.viewerImageUrl = url;
      this.imageViewerDialog = true;
    },
    getDriverAvatarUrl(driver) {
      if (!driver) return '/img/logo.194e35c5.png';
      if (driver.avatar_url) return driver.avatar_url;
      if (driver.avatar) {
        if (driver.avatar.startsWith('http://') || driver.avatar.startsWith('https://')) {
          return driver.avatar;
        }
        return (this.getApiUrl() || '') + (driver.avatar.startsWith('/') ? '' : '/') + driver.avatar;
      }
      return '/img/logo.194e35c5.png';
    },
    hasRating(item) {
      return item && item.rating_average !== null && item.rating_average !== undefined && Number(item.ratings_count) > 0;
    },
    formatRating(item) {
      if (!this.hasRating(item)) {
        return '--';
      }
      const avg = Number(item.rating_average);
      return isNaN(avg) ? '--' : avg.toFixed(1);
    },
    getStatusColor(status) {
      const statuses = [
        { color: "success" },
        { color: "warning" },
        { color: "error" },
        { color: "info" },
        { color: "error" },
      ];
      return statuses[status - 1] ? statuses[status - 1].color : 'grey';
    },
    getStatusValue(status) {
      const statuses = [
        { value: "Active" },
        { value: "Pending" },
        { value: "Suspended" },
        { value: "Under Review" },
        { value: "Rejected" },
      ];
      return statuses[status - 1] ? statuses[status - 1].value : 'Unknown';
    },
    returnToReviewUser(user) {
      this.$swal
        .fire({
          title: "إعادة السائق إلى المراجعة",
          text: `هل أنت تأكد من إعادة طلب السائق "${user.name}" إلى قائمة قيد المراجعة؟`,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "نعم، إعادة للمراجعة",
          cancelButtonText: "إلغاء",
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.isSubmit = true;
            axios
              .post('/drivers/take-action', {
                driver_id: user.id,
                action: 3
              })
              .then(() => {
                this.isSubmit = false;
                this.$notify({
                  title: "Success",
                  text: "تمت إعادة السائق إلى قائمة قيد المراجعة بنجاح",
                  type: "success",
                });
                this.loadUsers();
              })
              .catch((error) => {
                this.isSubmit = false;
                let msg = error.response?.data?.message || "حدث خطأ أثناء العملية";
                this.$swal("Error", msg, "error");
              });
          }
        });
    },
    fetchDriverOperationLog(driverId) {
      this.loadingLog = true;
      this.logError = false;
      axios
        .get(`/users/user/${driverId}/driver-operation-log`)
        .then((response) => {
          if (response.data && response.data.success) {
            this.operationLog = response.data;
            this.loadedDriverLogId = driverId;
          } else {
            this.logError = true;
          }
        })
        .catch((error) => {
          console.error(error);
          this.logError = true;
        })
        .finally(() => {
          this.loadingLog = false;
        });
    },
    getTransactionTypeName(type) {
      const types = {
        opening_balance: 'رصيد افتتاحى',
        trip_earning: 'أرباح رحلة',
        cash_collected: 'كاش محصل',
        payout: 'صرف أرباح',
        refund: 'مبالغ مستردة',
        adjustment: 'تسوية حساب'
      };
      return types[type] || type;
    },
    getTransactionTypeColor(type) {
      const colors = {
        opening_balance: 'primary',
        trip_earning: 'success',
        cash_collected: 'error',
        payout: 'info',
        refund: 'warning',
        adjustment: 'secondary'
      };
      return colors[type] || 'grey';
    },
    openAddDriverModal() {
      this.newDriver = {
        name: "",
        phone: "",
        email: "",
        password: "",
        password_confirmation: "",
        driver_type: "without_vehicle",
        working_status: "active",
        vehicle_type: "",
        vehicle_license_number: ""
      };
      if (this.$refs.addDriverForm) {
        this.$refs.addDriverForm.resetValidation();
      }
      this.addDriverDialog = true;
    },
    closeAddDriverModal() {
      this.addDriverDialog = false;
    },
    saveNewDriver() {
      if (!this.$refs.addDriverForm.validate()) return;
      this.isSavingDriver = true;
      
      const payload = { ...this.newDriver };
      if (payload.driver_type === 'without_vehicle') {
        payload.vehicle_type = null;
        payload.vehicle_license_number = null;
      }

      axios
        .post('/users/drivers', payload)
        .then((response) => {
          this.$notify({
            title: "نجاح",
            text: "تم إضافة السائق بنجاح",
            type: "success",
          });
          this.closeAddDriverModal();
          this.loadUsers();
        })
        .catch((error) => {
          console.error(error);
          let errMsg = "فشل إضافة السائق";
          if (error.response && error.response.data) {
            if (error.response.data.errors) {
              const errors = error.response.data.errors;
              errMsg = Object.values(errors).flat().join('\n');
            } else if (error.response.data.message) {
              errMsg = error.response.data.message;
            }
          }
          this.$swal("خطأ", errMsg, "error");
        })
        .then(() => {
          this.isSavingDriver = false;
        });
    }
  },
  computed: {
    workingStatusItems() {
      return [
        { text: this.$t('working_status_all') || 'الكل', value: 'all' },
        { text: this.$t('working_status_active') || 'نشط', value: 'active' },
        { text: this.$t('working_status_inactive') || 'غير نشط', value: 'inactive' }
      ];
    },
    summaryCards() {
      if (!this.operationLog || !this.operationLog.summary) return [];
      const s = this.operationLog.summary;
      return [
        {
          title: this.$t('completed_trips') || 'الرحلات المكتملة',
          value: s.completed_trips_count !== undefined ? s.completed_trips_count : 0,
          icon: 'mdi-check-circle-outline',
          color: 'success',
          bgColor: 'green lighten-5'
        },
        {
          title: this.$t('total_trip_revenue') || 'إجمالي إيراد الرحلات',
          value: (s.total_trip_revenue !== undefined ? s.total_trip_revenue : 0) + ' EGP',
          icon: 'mdi-currency-usd',
          color: 'primary',
          bgColor: 'blue lighten-5'
        },
        {
          title: this.$t('total_driver_earnings') || 'إجمالي أرباح السائق',
          value: (s.total_driver_earnings !== undefined ? s.total_driver_earnings : 0) + ' EGP',
          icon: 'mdi-cash-multiple',
          color: 'warning',
          bgColor: 'orange lighten-5'
        },
        {
          title: this.$t('total_cash_collected') || 'الكاش المحصل بواسطة السائق',
          value: (s.total_cash_collected !== undefined ? s.total_cash_collected : 0) + ' EGP',
          icon: 'mdi-cash-register',
          color: 'error',
          bgColor: 'red lighten-5'
        },
        {
          title: this.$t('total_paid_to_driver') || 'إجمالي المصروف للسائق',
          value: (s.total_paid_to_driver !== undefined ? s.total_paid_to_driver : 0) + ' EGP',
          icon: 'mdi-hand-coin',
          color: 'info',
          bgColor: 'cyan lighten-5'
        },
        {
          title: this.$t('current_wallet_balance') || 'صافي مستحق السائق الحالي',
          value: (s.current_wallet_balance !== undefined ? s.current_wallet_balance : 0) + ' EGP',
          icon: 'mdi-wallet',
          color: 'teal',
          bgColor: 'teal lighten-5'
        },
        {
          title: this.$t('company_net_revenue') || 'صافي الشركة من رحلات هذا السائق',
          value: (s.company_net_revenue !== undefined ? s.company_net_revenue : 0) + ' EGP',
          icon: 'mdi-trending-up',
          color: 'indigo',
          bgColor: 'indigo lighten-5'
        }
      ];
    }
  }
};
</script>
